<template>
  <div class="chat-layout card">
    <!-- メッセージ履歴 -->
    <div class="chat-history" ref="chatHistoryRef">
      <div
        v-for="msg in sortedChats"
        :key="msg.CHAT_NO"
        class="chat-message-row"
        :class="{
          'system-message': msg.MEMBER_CODE === 'SYSTEM',
          'my-message': msg.MEMBER_CODE !== 'SYSTEM' && isMyMessage(msg),
          'other-message': msg.MEMBER_CODE !== 'SYSTEM' && !isMyMessage(msg)
        }"
      >
        <!-- システムメッセージ -->
        <div v-if="msg.MEMBER_CODE === 'SYSTEM'" class="system-log-inner">
          <svg class="bell-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1-1.5-1s-1.5.17-1.5 1v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
          </svg>
          <span>{{ msg.MESSAGE_TEXT }}</span>
          <span class="message-time system-time">{{ formatTime(getMessageTimestamp(msg)) }}</span>
        </div>

        <!-- ユーザーメッセージ -->
        <template v-else>
          <div class="message-sender" v-if="!isMyMessage(msg)">
            {{ getMemberName(msg.MEMBER_CODE) }}
          </div>
          <div class="message-bubble">
            <p class="message-text">{{ msg.MESSAGE_TEXT }}</p>
            <span class="message-time">{{ formatTime(getMessageTimestamp(msg)) }}</span>
          </div>
        </template>
      </div>
    </div>

    <!-- 入力フォーム -->
    <form @submit.prevent="submitMessage" class="chat-input-form">
      <input
        v-model="newChatMessage"
        type="text"
        class="form-input chat-input"
        placeholder="メッセージを入力..."
        maxlength="1000"
        required
      />
      <button type="submit" class="btn-send-chat" :disabled="isLoading">
        送信
      </button>
    </form>
  </div>
</template>

<script>
import { ref, watch, nextTick, onMounted, computed } from "vue";

export default {
  name: "BoardChat",
  props: {
    chats: {
      type: Array,
      required: true,
    },
    members: {
      type: Array,
      required: true,
    },
    myUserCode: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["send-message"],
  setup(props, { emit }) {
    const newChatMessage = ref("");
    const chatHistoryRef = ref(null);

    const sortedChats = computed(() => {
      const chatNoValue = (value) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : String(value || "");
      };
      return [...props.chats].sort((a, b) => {
        const timeA = parseMessageDate(getMessageTimestamp(a))?.getTime() || 0;
        const timeB = parseMessageDate(getMessageTimestamp(b))?.getTime() || 0;
        if (timeA !== timeB) return timeA - timeB;
        const noA = chatNoValue(a.CHAT_NO);
        const noB = chatNoValue(b.CHAT_NO);
        if (typeof noA === "number" && typeof noB === "number") {
          return noA - noB;
        }
        return String(noA).localeCompare(String(noB), "ja", {
          numeric: true,
          sensitivity: "base",
        });
      });
    });

    const isNearBottom = () => {
      if (!chatHistoryRef.value) return false;
      const threshold = 100; // px
      const { scrollHeight, scrollTop, clientHeight } = chatHistoryRef.value;
      return scrollHeight - scrollTop - clientHeight <= threshold;
    };

    const scrollToBottom = () => {
      if (chatHistoryRef.value) {
        chatHistoryRef.value.scrollTop = chatHistoryRef.value.scrollHeight;
      }
    };

    const isMyMessage = (msg) => {
      const member = props.members.find((m) => m.MEMBER_CODE === msg.MEMBER_CODE);
      return (
        msg.MEMBER_CODE === props.myUserCode ||
        (member && member.APP_USER_CODE === props.myUserCode)
      );
    };

    const getMemberName = (memberCode) => {
      if (memberCode === "SYSTEM") return "システム";
      const m = props.members.find((member) => member.MEMBER_CODE === memberCode);
      return m ? m.MEMBER_NAME : memberCode;
    };

    function getMessageTimestamp(msg) {
      return (
        msg?.createdAt ||
        msg?.CREATED_AT ||
        msg?.created_at ||
        msg?.timestamp ||
        msg?.TIMESTAMP ||
        msg?.updatedAt ||
        msg?.UPDATED_AT ||
        msg?.updated_at ||
        msg?.ENTRY_YMD ||
        msg?.entryYmd ||
        msg?.UPDATE_YMD ||
        ""
      );
    }

    function isDateOnlyValue(value) {
      return /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim());
    }
    function parseMessageDate(value) {
      if (!value) return null;
      if (value instanceof Date) return value;
      const raw = String(value).trim();
      const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dateOnly) {
        return new Date(
          Number(dateOnly[1]),
          Number(dateOnly[2]) - 1,
          Number(dateOnly[3]),
          0,
          0,
          0,
        );
      }
      const localDateTime = raw.match(
        /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/,
      );
      if (localDateTime) {
        return new Date(
          Number(localDateTime[1]),
          Number(localDateTime[2]) - 1,
          Number(localDateTime[3]),
          Number(localDateTime[4]),
          Number(localDateTime[5]),
          Number(localDateTime[6] || 0),
        );
      }
      const parsed = new Date(raw);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const formatTime = (dateString) => {
      if (!dateString) return "日時不明";
      try {
        const d = parseMessageDate(dateString);
        if (!d) {
          return "日時不明";
        }
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        if (isDateOnlyValue(dateString)) {
          return `${year}/${month}/${day}`;
        }
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        return `${year}/${month}/${day} ${hours}:${minutes}`;
      } catch (e) {
        return "日時不明";
      }
    };

    const submitMessage = () => {
      if (!newChatMessage.value.trim()) return;
      emit("send-message", newChatMessage.value.trim());
      newChatMessage.value = "";
      // 自分が送信した後は即座にスクロール
      nextTick(() => {
        scrollToBottom();
      });
    };

    // チャット履歴が更新されたときのスクロール位置制御
    watch(sortedChats, (newChats) => {
      if (newChats.length > 0) {
        nextTick(() => {
          const lastChat = newChats[newChats.length - 1];
          const wasSentByMe = lastChat && isMyMessage(lastChat);
          if (wasSentByMe || isNearBottom()) {
            scrollToBottom();
          }
        });
      }
    }, { deep: true, immediate: true });

    onMounted(() => {
      nextTick(() => {
        scrollToBottom();
      });
    });

    return {
      newChatMessage,
      chatHistoryRef,
      isMyMessage,
      getMemberName,
      getMessageTimestamp,
      formatTime,
      submitMessage,
      sortedChats,
    };
  },
};
</script>

<style scoped>
.chat-layout {
  display: flex;
  flex-direction: column;
  height: 550px;
  overflow: hidden;
  padding: 0;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  box-shadow: var(--card-shadow);
}
.chat-history {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  background-color: var(--color-background);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.chat-message-row {
  display: flex;
  flex-direction: column;
  max-width: 70%;
}

.system-message {
  align-self: center;
  max-width: 90%;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid var(--color-border);
  border-radius: 30px;
  padding: 0.5rem 1.25rem;
  font-size: 0.75rem;
  color: var(--text-muted);
  font-weight: 700;
  text-align: center;
  box-shadow: var(--card-shadow);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.system-log-inner {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
}

.bell-icon {
  width: 14px;
  height: 14px;
  fill: var(--text-muted);
  flex-shrink: 0;
}

.my-message {
  align-self: flex-end;
  align-items: flex-end;
}

.other-message {
  align-self: flex-start;
  align-items: flex-start;
}

.message-sender {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-muted);
  margin-bottom: 0.25rem;
}

.message-bubble {
  background: #ffffff;
  border: 1px solid var(--color-border);
  padding: 0.75rem 1.15rem;
  border-radius: 18px;
  position: relative;
  box-shadow: var(--card-shadow);
}

.my-message .message-bubble {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-color: rgba(37, 99, 235, 0.18);
  border-top-right-radius: 4px;
}

.other-message .message-bubble {
  border-top-left-radius: 4px;
}

.message-text {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--text-main);
  line-height: 1.4;
  word-break: break-all;
}

.message-time {
  font-size: 0.6875rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
  display: block;
  text-align: right;
}

.system-time {
  margin-top: 0;
  white-space: nowrap;
}
.chat-input-form {
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  border-top: 1px solid var(--color-border);
  background-color: white;
}

.chat-input {
  flex: 1;
}

.btn-send-chat {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 0 1.5rem;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-send-chat:hover {
  background: var(--primary-hover);
}
</style>
